import { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useTranslate, useNotify, useRedirect, useDataProvider } from 'react-admin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ChannelType } from '../../../types/chat.types';
import { ZapyIcon } from '../../icons/ZapyIcon';
import { QueueSelector } from '../../queue/QueueSelector';

interface ZapyWizardProps {
  onBack: () => void;
}

const steps = ['channelInfo', 'finish'];

export const ZapyWizard = ({ onBack }: ZapyWizardProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [defaultQueueId, setDefaultQueueId] = useState<number | null>(null);
  const [createdChannelId, setCreatedChannelId] = useState<number | null>(null);

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!channelName.trim()) {
        notify('resources.channel.wizard.errors.nameRequired', { type: 'error' });
        return;
      }
      if (!defaultQueueId) {
        notify('resources.channel.wizard.errors.queueRequired', { type: 'error' });
        return;
      }

      setLoading(true);
      try {
        const { data } = await dataProvider.create('channel', {
          data: {
            name: channelName,
            type: ChannelType.ZAPY,
            defaultQueueId,
          },
        });
        setCreatedChannelId(data.id);
        setActiveStep(1);
        notify('resources.channel.wizard.success.channelCreated', { type: 'success' });
      } catch {
        notify('resources.channel.wizard.errors.createFailed', { type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFinish = () => {
    if (createdChannelId) {
      redirect('show', 'channel', createdChannelId);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 3 }}
      >
        {translate('ra.action.back')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(0, 184, 148, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ZapyIcon fontSize={28} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="medium">
              {translate('resources.channel.wizard.zapy.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.channel.wizard.zapy.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step}>
              <StepLabel>
                {translate(`resources.channel.wizard.steps.${step}`)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {translate('resources.channel.wizard.zapy.instructions')}
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label={translate('resources.channel.fields.name')}
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder={translate('resources.channel.wizard.zapy.namePlaceholder')}
              helperText={translate('resources.channel.wizard.zapy.nameHelper')}
              sx={{ mb: 3 }}
              required
            />

            <QueueSelector
              value={defaultQueueId}
              onChange={setDefaultQueueId}
              required
              allowClear={false}
              helperText={translate('resources.channel.wizard.queueHelper')}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || !channelName.trim() || !defaultQueueId}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  translate('resources.channel.wizard.createChannel')
                )}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {translate('resources.channel.wizard.zapy.successTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.zapy.successMessage')}
            </Typography>

            <Button variant="contained" onClick={handleFinish}>
              {translate('resources.channel.wizard.zapy.goToChannel')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
